// Utility functions for profile image uploads with signed URLs

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Get signed URL for profile image upload
 * @param {File} file - The image file to upload
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Response with signed URL
 */
export const getProfileImageSignedUrl = async (file, token) => {
  const response = await fetch(`${API_URL}/upload/profile-image-signed-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      originalName: file.name,
      contentType: file.type,
      size: file.size
    })
  });

  if (!response.ok) {
    const error = await response.json();
    
    // Check if this is a service account issue and we should fall back
    if (response.status === 503 && error.fallbackToRegularUpload) {
      throw new Error('SIGNED_URLS_UNAVAILABLE: ' + error.message);
    }
    
    throw new Error(error.message || 'Failed to get profile image upload URL');
  }

  return response.json();
};

/**
 * Upload profile image directly to Cloud Storage using signed URL
 * @param {File} file - The image file to upload
 * @param {string} signedUrl - The signed URL for upload
 * @param {string} contentType - The MIME type of the file
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} - Upload promise
 */
export const uploadProfileImageToSignedUrl = async (file, signedUrl, contentType, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    
    // Important: Set the ACL to make the file public
    xhr.setRequestHeader('x-goog-acl', 'public-read');
    
    xhr.send(file);
  });
};

/**
 * Update user profile with new image URL (after successful upload)
 * @param {string} profileImageUrl - The public URL of the uploaded image
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Updated user data
 */
export const updateProfileImageDirect = async (profileImageUrl, token) => {
  const response = await fetch(`${API_URL}/auth/profile/image-direct`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ profileImageUrl })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update profile image');
  }

  return response.json();
};

/**
 * Complete profile image upload workflow with signed URLs
 * @param {File} file - The image file to upload
 * @param {string} token - Authentication token
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Updated user data
 */
export const uploadProfileImageWithSignedUrl = async (file, token, onProgress) => {
  try {
    // Step 1: Get signed URL
    onProgress && onProgress(10, 'Getting upload URL...');
    const signedUrlResponse = await getProfileImageSignedUrl(file, token);
    
    if (!signedUrlResponse.success) {
      throw new Error('Failed to get signed URL');
    }

    // Step 2: Upload directly to Cloud Storage
    onProgress && onProgress(20, 'Uploading image...');
    await uploadProfileImageToSignedUrl(
      file, 
      signedUrlResponse.file.signedUrl, 
      file.type,
      (progress) => {
        // Map progress from 20% to 90%
        const mappedProgress = 20 + (progress * 0.7);
        onProgress && onProgress(mappedProgress, 'Uploading image...');
      }
    );

    // Step 3: Update profile with new image URL
    onProgress && onProgress(95, 'Updating profile...');
    const updateResult = await updateProfileImageDirect(signedUrlResponse.file.publicUrl, token);
    
    onProgress && onProgress(100, 'Complete!');
    return updateResult;

  } catch (error) {
    throw error;
  }
};

/**
 * Validate profile image file
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result
 */
export const validateProfileImage = (file) => {
  const errors = [];
  
  // Size validation (50MB limit for signed URL uploads)
  if (file.size > 50 * 1024 * 1024) {
    errors.push('Profile image must be less than 50MB');
  }
  
  // Type validation
  if (!file.type.startsWith('image/')) {
    errors.push('File must be an image');
  } else if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    errors.push('Profile image must be in JPEG, PNG, or WebP format');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if file should use signed URL upload (>10MB)
 * @param {File} file - The file to check
 * @returns {boolean} - Whether to use signed URL upload
 */
export const shouldUseSignedUrlUpload = (file) => {
  return file.size > 10 * 1024 * 1024; // 10MB threshold
};
